import { APP_URLS, PRODUCT_NAME } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, MAIN_CONTENT_ID, SkipLink, useTheme } from "@sagot/ui";
import { useEffect, type ReactNode } from "react";
import "./auth.css";
import "./auth-register.css";
import { SagotLogoMark } from "./components/brand-icons";
import { AuthLogo } from "./components/auth-logo";
import { AuthThemeSwitch } from "./components/theme-switch";
import { BACKDROP_MESSAGES, BACKDROP_NAV, BACKDROP_THREADS, CONSOLE_BACKDROP_LABEL, REGISTER_NUMBERED_STEPS } from "./register-constants";
import { CloseGlyph } from "./register-icons";

/** The blurred console behind the card (mockup `.r-console`): pure decoration, hidden from assistive technology. */
function RegisterBackdrop() {
  return (
    <>
      <div className="r-console" aria-hidden="true">
        <div className="r-sb">
          <b>
            <span className="r-sb-mark">
              <SagotLogoMark tile={20} />
            </span>
            {CONSOLE_BACKDROP_LABEL}
          </b>
          {BACKDROP_NAV.map((item) => (
            <span key={item.label} className={"active" in item ? "on" : undefined}>
              {item.label}
              {"count" in item ? <em>{item.count}</em> : null}
            </span>
          ))}
        </div>
        <div className="r-ls">
          {BACKDROP_THREADS.map((thread) => (
            <div key={thread.who}>
              <b>{thread.who}</b>
              <small>{thread.text}</small>
            </div>
          ))}
        </div>
        <div className="r-cv">
          {BACKDROP_MESSAGES.map((message) => (
            <div key={message.text} className={message.mine ? "m u" : "m"}>
              {message.text}
            </div>
          ))}
        </div>
      </div>
      <div className="r-scrim" />
    </>
  );
}

interface RegisterShellProps {
  /** 1 to 6: position in `REGISTER_STEPS` (drives the progress bar and the "Step n of 5" label). */
  position: number;
  /** Document title. */
  title: string;
  children: ReactNode;
}

function useDocumentTitle(title: string) {
  useEffect(() => {
    document.title = title;
  }, [title]);
}

/**
 * Page of the register wizard (mockup `data-page="register"`): sand page, the blurred console, a top bar, the card with its
 * progress bar and close button, and the trust line under it. Token scope and light / dark come from `.auth-shell[data-theme]`.
 */
export function RegisterShell({ position, title, children }: RegisterShellProps) {
  const { t } = useTranslation();
  const { resolved } = useTheme();
  useDocumentTitle(title);
  return (
    <div className="auth-shell a-page r-page" data-theme={resolved}>
      <SkipLink label={t("shell.skipToContent")} />
      <RegisterBackdrop />
      <header className="a-top">
        <AppLink href="/" className="a-logo-link" aria-label={t("auth.aside.homeLabel", { product: PRODUCT_NAME })}>
          <AuthLogo />
        </AppLink>
        <div className="a-top-end">
          <span className="a-top-text">{t("auth.common.alreadyHaveAccount")}</span>
          <AppLink href="/login" className="a-top-link">
            {t("auth.common.signIn")}
          </AppLink>
          <AuthThemeSwitch />
        </div>
      </header>
      <main id={MAIN_CONTENT_ID} tabIndex={-1} className="r-wrap">
        <section className="r-modal" aria-labelledby="r-title">
          <span className="r-prog" data-position={position} aria-hidden="true" />
          <a className="r-close" href={APP_URLS.marketing} aria-label={t("auth.register.close")}>
            <CloseGlyph />
          </a>
          <div className="r-in">
            <div className="r-step">{position <= REGISTER_NUMBERED_STEPS ? t("auth.register.stepOf", { n: position, total: REGISTER_NUMBERED_STEPS }) : null}</div>
            {children}
          </div>
        </section>
      </main>
      <div className="r-trust">
        <span className="mono">{t("auth.register.trust")}</span>
      </div>
    </div>
  );
}
